import { createSlice, createSelector, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "./UiDb";
import { resetAllState } from "./resetAllState";
import { ProductDetailsModal } from "../../interfaces/ProductObject";
import { ListingCollection } from "../../interfaces/ListingCollectionObject";
import {
  fetchListingCollectionById,
  fetchMyListingCollections,
  createListingCollection,
  updateListingCollection,
  deleteListingCollection,
} from "../../services/thunks/ListingCollectionThunk";
import { ListingCollectionErrorCodes } from "../../interfaces/ListingCollectionObject";

/**
 * One collection's own state: its metadata plus the expanded product listings
 * inside it — both filled by a single fetchListingCollectionById.
 *
 * `myCollections` is the curator's own collections for the Admin screen.
 *
 * Parallels BusinessCollectionDb exactly, with `businesses` replaced by
 * `products` and business-specific selectors removed.
 */
interface ListingCollectionState {
  collection: ListingCollection | null;
  products: ProductDetailsModal[];
  /** NOT_FOUND error code — a wrong id. Distinct from a transient failure. */
  notFound: boolean;
  /** The curator's own collections (Admin → My Listing Collections). */
  myCollections: ListingCollection[];
  loadingMap: Record<string, string | null>;
  errorMap: Record<string, string | null>;
  successMap: Record<string, boolean>;
}

const initialState: ListingCollectionState = {
  collection: null,
  products: [],
  notFound: false,
  myCollections: [],
  loadingMap: {},
  errorMap: {},
  successMap: {},
};

const listingCollectionSlice = createSlice({
  name: "listingCollection",
  initialState,
  reducers: {
    /** Seeds metadata the caller already holds (search result → router state). */
    setListingCollectionMeta(state, action: PayloadAction<ListingCollection>) {
      state.collection = action.payload;
    },
    clearListingCollection() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // fetchListingCollectionById
    builder
      .addCase(fetchListingCollectionById.pending, (state) => {
        state.loadingMap.fetchCollection = "Loading collection...";
        state.errorMap.fetchCollection = null;
        state.successMap.fetchCollection = false;
        state.notFound = false;
      })
      .addCase(fetchListingCollectionById.fulfilled, (state, action) => {
        state.loadingMap.fetchCollection = null;
        state.successMap.fetchCollection = true;
        state.collection = action.payload.collection;
        state.products = action.payload.products;
      })
      .addCase(fetchListingCollectionById.rejected, (state, action) => {
        state.loadingMap.fetchCollection = null;
        state.products = [];
        state.errorMap.fetchCollection =
          action.error?.message ?? "Couldn't load this collection.";
        state.notFound =
          action.error?.code === ListingCollectionErrorCodes.NOT_FOUND;
      });

    // fetchMyListingCollections (Admin → My Listing Collections list)
    builder
      .addCase(fetchMyListingCollections.pending, (state) => {
        state.loadingMap.fetchMyCollections = "Loading your collections...";
        state.errorMap.fetchMyCollections = null;
        state.successMap.fetchMyCollections = false;
      })
      .addCase(fetchMyListingCollections.fulfilled, (state, action) => {
        state.loadingMap.fetchMyCollections = null;
        state.successMap.fetchMyCollections = true;
        state.myCollections = action.payload;
      })
      .addCase(fetchMyListingCollections.rejected, (state, action) => {
        state.loadingMap.fetchMyCollections = null;
        state.errorMap.fetchMyCollections =
          action.error?.message ?? "Couldn't load your collections.";
      });

    // createListingCollection
    builder
      .addCase(createListingCollection.pending, (state) => {
        state.loadingMap.createCollection = "Creating collection...";
        state.errorMap.createCollection = null;
        state.successMap.createCollection = false;
      })
      .addCase(createListingCollection.fulfilled, (state, action) => {
        state.loadingMap.createCollection = null;
        state.successMap.createCollection = true;
        state.myCollections.unshift(action.payload);
      })
      .addCase(createListingCollection.rejected, (state, action) => {
        state.loadingMap.createCollection = null;
        state.errorMap.createCollection =
          action.error?.message ?? "Couldn't create this collection.";
      });

    // updateListingCollection
    builder
      .addCase(updateListingCollection.pending, (state) => {
        state.loadingMap.updateCollection = "Saving changes...";
        state.errorMap.updateCollection = null;
        state.successMap.updateCollection = false;
      })
      .addCase(updateListingCollection.fulfilled, (state, action) => {
        state.loadingMap.updateCollection = null;
        state.successMap.updateCollection = true;
        const updated = action.payload;
        const idx = state.myCollections.findIndex((c) => c.uid === updated.uid);
        if (idx >= 0) state.myCollections[idx] = updated;
        if (state.collection?.uid === updated.uid) state.collection = updated;
      })
      .addCase(updateListingCollection.rejected, (state, action) => {
        state.loadingMap.updateCollection = null;
        state.errorMap.updateCollection =
          action.error?.message ?? "Couldn't update this collection.";
      });

    // deleteListingCollection — per-row key so deleting one row doesn't
    // disable every other row's delete button.
    builder
      .addCase(deleteListingCollection.pending, (state, action) => {
        const key = `deleteCollection_${action.meta.arg}`;
        state.loadingMap[key] = "Deleting...";
        state.errorMap[key] = null;
        state.successMap[key] = false;
      })
      .addCase(deleteListingCollection.fulfilled, (state, action) => {
        const key = `deleteCollection_${action.meta.arg}`;
        state.loadingMap[key] = null;
        state.successMap[key] = true;
        state.myCollections = state.myCollections.filter(
          (c) => c.uid !== action.payload
        );
        if (state.collection?.uid === action.payload) {
          state.collection = null;
          state.products = [];
        }
      })
      .addCase(deleteListingCollection.rejected, (state, action) => {
        const key = `deleteCollection_${action.meta.arg}`;
        state.loadingMap[key] = null;
        state.errorMap[key] =
          action.error?.message ?? "Couldn't delete this collection.";
      });

    builder.addCase(resetAllState, () => initialState);
  },
});

export const { setListingCollectionMeta, clearListingCollection } =
  listingCollectionSlice.actions;

export default listingCollectionSlice.reducer;

// ─── Selectors ───────────────────────────────────────────────────────────────

const selectSlice = (state: RootState) => state.listingCollection;

export const selectListingCollectionProducts = createSelector(
  [selectSlice],
  (slice) => slice.products
);

export const selectListingCollectionStatus = createSelector(
  [selectSlice],
  (slice) => ({
    isLoading: Boolean(slice.loadingMap.fetchCollection),
    hasLoaded: Boolean(slice.successMap.fetchCollection),
    error: slice.errorMap.fetchCollection ?? null,
    notFound: slice.notFound,
  })
);

// No search-cache fallback here — unlike BusinessCollectionDb, there is no
// listing-collection search tab on the Search page. The only search surface
// is NineSetupBulkActions, which manages its own request lifecycle.
export const selectListingCollectionMeta = createSelector(
  [selectSlice],
  (slice) => slice.collection
);

// ─── My Collections (Admin) selectors ────────────────────────────────────────

export const selectMyListingCollections = createSelector(
  [selectSlice],
  (slice) => slice.myCollections
);

export const selectMyListingCollectionsStatus = createSelector(
  [selectSlice],
  (slice) => ({
    isLoading: Boolean(slice.loadingMap.fetchMyCollections),
    hasLoaded: Boolean(slice.successMap.fetchMyCollections),
    error: slice.errorMap.fetchMyCollections ?? null,
  })
);

export const selectCreateListingCollectionStatus = createSelector(
  [selectSlice],
  (slice) => ({
    isSubmitting: Boolean(slice.loadingMap.createCollection),
    error: slice.errorMap.createCollection ?? null,
  })
);

export const selectUpdateListingCollectionStatus = createSelector(
  [selectSlice],
  (slice) => ({
    isSubmitting: Boolean(slice.loadingMap.updateCollection),
    error: slice.errorMap.updateCollection ?? null,
  })
);

/** Per-row delete status for a given collection id. */
export const selectDeleteListingCollectionStatus = createSelector(
  [selectSlice, (_: RootState, collectionId: string) => collectionId],
  (slice, collectionId) => ({
    isDeleting: Boolean(slice.loadingMap[`deleteCollection_${collectionId}`]),
    error: slice.errorMap[`deleteCollection_${collectionId}`] ?? null,
  })
);
